import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Wand2, CheckCircle2, Circle, FolderPlus, Package, GripVertical, ChevronRight, ChevronDown, Pencil, Check } from 'lucide-react';
import * as C from '../constants';
import api, { getToken } from '../api';
import AlertToast from './AlertToast';
import ConfirmModal from './ConfirmModal';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

/**
 * PresetModal — 预设工具箱配置弹窗（超管），三层结构
 *
 * 工具集（toolset）→ 分组（group）→ 预设（preset）
 *
 * 左侧：三层树（工具集可建/删，分组挂在工具集下可建/删，预设挂在分组下可拖拽排序）
 *       组内排序最靠前的预设 = 该工具集在画布工具条上显示的「置顶预设」图标
 * 右侧：预设配置表单（LOGO / 名称 / 工具集下拉 / 分组下拉「随工具集联动」/ 提示词 / 描述词 / 启用）
 */

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4100 };
const modalStyle = { width: 1080, height: '84vh', background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, color: 'var(--text-body)', boxShadow: 'var(--shadow-panel)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--modal-input-bg)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const btn = (primary) => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? '#3b82f6' : 'var(--border-strong)'), background: primary ? '#3b82f6' : 'transparent', color: primary ? '#fff' : 'var(--text-body)', cursor: 'pointer', fontSize: 13 });

const emptyForm = { id: null, name: '', toolsetId: null, groupId: null, logoUrl: '', prompt: '', description: '', modelIds: [], enabled: true };

export default function PresetModal({ open, onClose }) {
  const [tree, setTree] = useState([]);            // [{id,name,groups:[{id,name,presets:[]}]}]
  const [imageModels, setImageModels] = useState([]);   // 可选图像模型（供预设勾选白名单）
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [newToolsetName, setNewToolsetName] = useState('');
  const [showToolsetInput, setShowToolsetInput] = useState(false);   // 顶部「新增工具集」输入行开关
  const [renameToolsetId, setRenameToolsetId] = useState(null);      // 正在改名的工具集 id
  const [renameToolsetVal, setRenameToolsetVal] = useState('');      // 改名输入值
  const [groupInputFor, setGroupInputFor] = useState(null);  // 正在新增分组的 toolsetId
  const [newGroupName, setNewGroupName] = useState('');
  const [renameGroupId, setRenameGroupId] = useState(null);      // 正在改名的分组 id
  const [renameGroupVal, setRenameGroupVal] = useState('');      // 分组改名输入值
  const [collapsed, setCollapsed] = useState({});            // { [toolsetId]: true } 折叠态
  const [dragInfo, setDragInfo] = useState(null);            // { presetId, groupId }
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [confirm, setConfirm] = useState(null);             // { kind, id, name }

  const showToast = (msg, type = 'success', duration = 4000) => {
    setToastMessage(msg); setToastType(type);
    setTimeout(() => setToastMessage(''), duration);
  };
  const notifyUpdated = () => window.dispatchEvent(new Event('presetsUpdated'));

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/presets');
      setTree(data.toolsets || []);
    } catch (e) {
      showToast('❌ 加载预设失败：' + (e.message || '未知错误'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  // 图像模型列表：与画布节点下拉同源（/api/models/by-type/image，已排除 toolbox 模型）
  const loadImageModels = async () => {
    try {
      const list = await api.get('/api/models/by-type/image');
      setImageModels(Array.isArray(list) ? list : []);
    } catch (e) {
      setImageModels([]);
      showToast('❌ 加载图像模型失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  useEffect(() => { if (open) { loadData(); loadImageModels(); setForm(emptyForm); setLogoLoadError(false); setNewToolsetName(''); setShowToolsetInput(false); setRenameToolsetId(null); setGroupInputFor(null); setNewGroupName(''); setRenameGroupId(null); } }, [open]);

  if (!open) return null;

  // 工具集 / 分组下拉数据
  const toolsetOptions = tree.map(t => ({ id: t.id, name: t.name }));
  const groupOptionsForToolset = (toolsetId) => {
    const t = tree.find(x => String(x.id) === String(toolsetId));
    return t ? (t.groups || []).map(g => ({ id: g.id, name: g.name })) : [];
  };

  const selectPreset = (p) => {
    setForm({ id: p.id, name: p.name, toolsetId: p.toolsetId, groupId: p.groupId, logoUrl: p.logoUrl, prompt: p.prompt, description: p.description, modelIds: Array.isArray(p.modelIds) ? p.modelIds : [], enabled: p.enabled });
    setLogoLoadError(false);
  };
  const newPreset = (toolsetId = null, groupId = null) => { setForm({ ...emptyForm, toolsetId, groupId }); setLogoLoadError(false); };

  // ── 预设：保存 / 启用切换 / 删除 ──
  const handleSave = async () => {
    if (!form.name.trim()) { showToast('⚠️ 请填写预设名', 'warning'); return; }
    if (form.groupId == null) { showToast('⚠️ 请选择所属工具集和分组', 'warning'); return; }
    setSaving(true);
    try {
      await api.post('/api/admin/presets', {
        id: form.id || undefined,
        name: form.name.trim(),
        groupId: form.groupId,
        logoUrl: form.logoUrl || null,
        prompt: form.prompt,
        description: form.description,
        modelIds: form.modelIds || [],
        enabled: form.enabled,
      });
      showToast(form.id ? '✅ 已更新' : '✅ 已新增', 'success', 2500);
      await loadData();
      notifyUpdated();
      if (!form.id) setForm(emptyForm);
    } catch (e) {
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreset = async (p) => {
    try {
      await api.post('/api/admin/presets', { id: p.id, name: p.name, groupId: p.groupId, logoUrl: p.logoUrl, prompt: p.prompt, description: p.description, modelIds: p.modelIds || [], enabled: !p.enabled });
      if (form.id === p.id) setForm(f => ({ ...f, enabled: !f.enabled }));
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 切换失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  // ── 工具集：新增 / 改名 ──
  const handleAddToolset = async () => {
    const name = newToolsetName.trim();
    if (!name) return;
    try {
      await api.post('/api/admin/preset-toolsets', { name });
      setNewToolsetName('');
      setShowToolsetInput(false);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '新增工具集失败'), 'warning'); }
  };

  const handleRenameToolset = async (toolsetId) => {
    const name = renameToolsetVal.trim();
    if (!name) { setRenameToolsetId(null); return; }
    try {
      await api.post('/api/admin/preset-toolsets', { id: toolsetId, name });
      setRenameToolsetId(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '改名失败'), 'warning'); }
  };

  const handleAddGroup = async (toolsetId) => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await api.post('/api/admin/preset-groups', { name, toolsetId });
      setNewGroupName(''); setGroupInputFor(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '新增分组失败'), 'warning'); }
  };

  const handleRenameGroup = async (groupId) => {
    const name = renameGroupVal.trim();
    if (!name) { setRenameGroupId(null); return; }
    try {
      await api.post('/api/admin/preset-groups', { id: groupId, name });
      setRenameGroupId(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '改名失败'), 'warning'); }
  };

  // ── 删除（工具集 / 分组 / 预设）──
  const handleDelete = async () => {
    if (!confirm) return;
    const { kind, id } = confirm;
    try {
      if (kind === 'toolset') await api.delete(`/api/admin/preset-toolsets/${id}`);
      else if (kind === 'group') await api.delete(`/api/admin/preset-groups/${id}`);
      else { await api.delete(`/api/admin/presets/${id}`); if (form.id === id) setForm(emptyForm); }
      showToast('✅ 已删除', 'success', 2500);
      setConfirm(null);
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 删除失败：' + (e.message || '未知错误'), 'warning');
      setConfirm(null);
    }
  };

  // ── 预设拖拽排序（组内）──
  const handleDropOnPreset = async (targetPreset) => {
    if (!dragInfo || dragInfo.presetId === targetPreset.id) { setDragInfo(null); return; }
    if (dragInfo.groupId !== targetPreset.groupId) { setDragInfo(null); return; } // 仅支持组内排序
    const group = tree.flatMap(t => t.groups).find(g => g.id === targetPreset.groupId);
    if (!group) { setDragInfo(null); return; }
    const ids = group.presets.map(p => p.id);
    const from = ids.indexOf(dragInfo.presetId);
    const to = ids.indexOf(targetPreset.id);
    if (from < 0 || to < 0) { setDragInfo(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const order = ids.map((id, idx) => ({ id, sortOrder: idx }));
    setDragInfo(null);
    try {
      await api.put('/api/admin/presets/order', { order });
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ 排序保存失败', 'warning'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) {
          setForm(f => ({ ...f, logoUrl: checkResult.url })); setLogoLoadError(false); setLogoUploading(false); e.target.value = ''; return;
        }
      }
      const formData = new FormData(); formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: formData }).then(r => r.json());
      if (result.url) { setForm(f => ({ ...f, logoUrl: result.url })); setLogoLoadError(false); }
      else if (result.error) { showToast('❌ 上传失败: ' + result.error, 'warning'); }
    } catch (err) {
      console.error('上传 Logo 失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    setLogoUploading(false);
    e.target.value = '';
  };

  const modalContent = (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wand2 size={16} color="#daa520" /> 预设工具箱</span>
          <X size={18} style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* 左：三层树 */}
          <div style={{ width: 380, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: 12, borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* 顶部大按钮：新增工具集 */}
              {!showToolsetInput ? (
                <button onClick={() => { setShowToolsetInput(true); setNewToolsetName(''); }} style={{ ...btn(true), width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Package size={14} /> 新增工具集
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input autoFocus style={{ ...inputStyle, padding: '7px 10px' }} value={newToolsetName}
                    onChange={e => setNewToolsetName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddToolset(); if (e.key === 'Escape') setShowToolsetInput(false); }}
                    placeholder="工具集名称" />
                  <button onClick={handleAddToolset} style={{ ...btn(true), padding: '7px 12px', flexShrink: 0 }}>添加</button>
                  <button onClick={() => setShowToolsetInput(false)} style={{ ...btn(false), padding: '7px 10px', flexShrink: 0 }}>取消</button>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.5 }}>层级：工具集 → 分组 → 预设。工具集后「<FolderPlus size={9} style={{ verticalAlign: 'middle' }} />」加分组，分组后「<Plus size={9} style={{ verticalAlign: 'middle' }} />」加预设。</div>
            </div>
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {loading ? (
                <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '20px 0' }}>加载中...</div>
              ) : tree.length === 0 ? (
                <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '20px 0' }}>暂无工具集，点上方「新增工具集」</div>
              ) : tree.map(toolset => {
                const isCollapsed = collapsed[toolset.id];
                const isRenaming = renameToolsetId === toolset.id;
                return (
                  <div key={toolset.id} style={{ marginBottom: 8 }}>
                    {/* 工具集行 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 4px', borderRadius: 6, background: 'rgba(218,165,32,0.08)' }}>
                      <span onClick={() => setCollapsed(c => ({ ...c, [toolset.id]: !c[toolset.id] }))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {isCollapsed ? <ChevronRight size={14} color="#daa520" /> : <ChevronDown size={14} color="#daa520" />}
                      </span>
                      <Package size={14} color="#daa520" style={{ flexShrink: 0 }} />
                      {isRenaming ? (
                        <>
                          <input autoFocus style={{ ...inputStyle, flex: 1, padding: '3px 8px', fontSize: 12 }} value={renameToolsetVal}
                            onChange={e => setRenameToolsetVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameToolset(toolset.id); if (e.key === 'Escape') setRenameToolsetId(null); }}
                            onClick={e => e.stopPropagation()} />
                          <button onClick={() => handleRenameToolset(toolset.id)} title="保存"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={14} color="#10b981" />
                          </button>
                          <button onClick={() => setRenameToolsetId(null)} title="取消"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={14} color="#64748b" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#e2c98a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toolset.name}</span>
                          <button onClick={() => { setRenameToolsetId(toolset.id); setRenameToolsetVal(toolset.name); }} title="重命名工具集"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Pencil size={12} color="var(--text-secondary)" />
                          </button>
                          <button onClick={() => { setGroupInputFor(groupInputFor === toolset.id ? null : toolset.id); setNewGroupName(''); }} title="新增分组"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FolderPlus size={14} color="var(--text-secondary)" />
                          </button>
                          <button onClick={() => setConfirm({ kind: 'toolset', id: toolset.id, name: toolset.name })} title="删除工具集"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={13} color="#64748b" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* 新增分组输入框 */}
                    {groupInputFor === toolset.id && (
                      <div style={{ display: 'flex', gap: 6, padding: '6px 4px 6px 22px' }}>
                        <input autoFocus style={{ ...inputStyle, padding: '6px 8px', fontSize: 12 }} value={newGroupName}
                          onChange={e => setNewGroupName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddGroup(toolset.id); if (e.key === 'Escape') setGroupInputFor(null); }}
                          placeholder="分组名" />
                        <button onClick={() => handleAddGroup(toolset.id)} style={{ ...btn(true), padding: '6px 10px', fontSize: 12 }}>添加</button>
                      </div>
                    )}

                    {/* 分组 + 预设 */}
                    {!isCollapsed && (toolset.groups || []).map(group => {
                      const isRenamingGroup = renameGroupId === group.id;
                      return (
                      <div key={group.id} style={{ paddingLeft: 14, marginTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 4px', borderBottom: '1px solid var(--border-subtle)' }}>
                          {isRenamingGroup ? (
                            <>
                              <input autoFocus style={{ ...inputStyle, flex: 1, padding: '3px 8px', fontSize: 11 }} value={renameGroupVal}
                                onChange={e => setRenameGroupVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleRenameGroup(group.id); if (e.key === 'Escape') setRenameGroupId(null); }}
                                onClick={e => e.stopPropagation()} />
                              <button onClick={() => handleRenameGroup(group.id)} title="保存"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Check size={13} color="#10b981" />
                              </button>
                              <button onClick={() => setRenameGroupId(null)} title="取消"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={13} color="#64748b" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1, fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                              <button onClick={() => { setRenameGroupId(group.id); setRenameGroupVal(group.name); }} title="重命名分组"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Pencil size={11} color="var(--text-secondary)" />
                              </button>
                              <button onClick={() => newPreset(toolset.id, group.id)} title="在此分组新增预设"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Plus size={13} color="var(--text-secondary)" />
                              </button>
                              <button onClick={() => setConfirm({ kind: 'group', id: group.id, name: group.name })} title="删除分组（含组内预设）"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Trash2 size={12} color="#64748b" />
                              </button>
                            </>
                          )}
                        </div>
                        {group.presets.length === 0 ? (
                          <div style={{ fontSize: 10, color: '#475569', padding: '4px 8px' }}>（空，点 + 添加预设）</div>
                        ) : group.presets.map((p, idx) => {
                          const ep = { ...p, toolsetId: toolset.id };  // 补 toolsetId 供表单回填
                          const isActive = form.id === p.id;
                          const isTop = idx === 0;
                          return (
                            <div key={p.id}
                              draggable
                              onDragStart={() => setDragInfo({ presetId: p.id, groupId: group.id })}
                              onDragOver={e => e.preventDefault()}
                              onDrop={() => handleDropOnPreset({ ...p, groupId: group.id })}
                              onClick={() => selectPreset(ep)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', paddingRight: 52, marginBottom: 3, marginLeft: 8, borderRadius: 6, cursor: 'pointer', position: 'relative',
                                background: isActive ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)',
                                border: `1px solid ${isActive ? '#3b82f6' : 'transparent'}`, transition: 'all 0.15s' }}
                              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--modal-input-bg)'; }}>
                              <GripVertical size={13} color="#475569" style={{ flexShrink: 0, cursor: 'grab' }} />
                              {p.logoUrl ? (
                                <img src={p.logoUrl} alt={p.name} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3 }} onError={e => { e.target.style.display = 'none'; }} />
                              ) : (<Wand2 size={14} color="var(--text-secondary)" style={{ flexShrink: 0 }} />)}
                              <span style={{ flex: 1, fontSize: 12, color: isActive ? 'var(--text-body)' : 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: p.enabled ? 1 : 0.5 }}>{p.name}</span>
                              {isTop && <span title="置顶预设：作为该工具集在工具条上的图标" style={{ fontSize: 8, color: '#daa520', border: '1px solid rgba(218,165,32,0.4)', borderRadius: 3, padding: '0 3px', flexShrink: 0 }}>顶</span>}
                              <button onClick={(e) => { e.stopPropagation(); handleTogglePreset(p); }}
                                style={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title={p.enabled ? '点击禁用' : '点击启用'}>
                                {p.enabled ? <CheckCircle2 size={16} color="#10b981" /> : <Circle size={16} color="#6b7280" />}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); setConfirm({ kind: 'preset', id: p.id, name: p.name }); }}
                                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="删除">
                                <Trash2 size={13} color="#f87171" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 右：配置表单 */}
          <div className="custom-scrollbar" style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-body)', marginBottom: 16 }}>{form.id ? '编辑预设' : '新增预设'}</div>

            {/* LOGO */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>预设 LOGO（节点下拉图标；组内置顶预设作为工具集图标显示在工具条）</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {form.logoUrl && !logoLoadError && <img src={form.logoUrl} onError={() => setLogoLoadError(true)} onLoad={() => setLogoLoadError(false)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />}
                  <Wand2 size={20} style={{ color: '#64748b', opacity: 0.5, display: (form.logoUrl && !logoLoadError) ? 'none' : 'flex' }} />
                </div>
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="preset-logo-upload" onChange={handleLogoUpload} />
                <label htmlFor="preset-logo-upload" style={{ padding: '8px 16px', background: logoUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: logoUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                  {logoUploading ? '上传中...' : '上传 LOGO'}
                </label>
                {form.logoUrl && <button onClick={() => { setForm(f => ({ ...f, logoUrl: '' })); setLogoLoadError(false); }} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>支持 PNG、JPG、SVG、WebP，建议 48x48 或更大</div>
            </div>

            {/* 名称 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>预设名 *</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="如：九宫格、角色定妆" />
            </div>

            {/* 工具集 + 分组（只能选；分组随工具集联动） */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>所属工具集 *（左侧新建）</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.toolsetId == null ? '' : form.toolsetId}
                  onChange={e => {
                    const tid = e.target.value === '' ? null : Number(e.target.value);
                    // 切工具集时清空分组（强制重新选当前工具集下的分组）
                    setForm(f => ({ ...f, toolsetId: tid, groupId: null }));
                  }}>
                  <option value="">请选择工具集</option>
                  {toolsetOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>所属分组 *</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.groupId == null ? '' : form.groupId}
                  disabled={form.toolsetId == null}
                  onChange={e => setForm(f => ({ ...f, groupId: e.target.value === '' ? null : Number(e.target.value) }))}>
                  <option value="">{form.toolsetId == null ? '请先选工具集' : '请选择分组'}</option>
                  {groupOptionsForToolset(form.toolsetId).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>

            {/* 提示词 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>附加提示词 *（生图时拼在用户输入最前，注入大模型）</label>
              <textarea style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={form.prompt} onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                placeholder="如：将参考图人物生成 3x3 九宫格不同角度表情，统一画风..." />
            </div>

            {/* 描述词 */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>描述词（生成节点输入框灰色提示文字）</label>
              <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="如：可补充表情 / 服装等细节，留空将使用预设默认效果" />
            </div>

            {/* 可用模型：勾选后，用户在画布点该预设弹出的生成节点里只能选这些模型 */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>可用图像模型（不勾选 = 不限制，显示全部）</label>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => setForm(f => ({ ...f, modelIds: imageModels.map(m => m.id) }))}
                    style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 11, cursor: 'pointer', padding: 0 }}>全选</button>
                  <button onClick={() => setForm(f => ({ ...f, modelIds: [] }))}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 11, cursor: 'pointer', padding: 0 }}>清空</button>
                </div>
              </div>
              <div className="custom-scrollbar" style={{ maxHeight: 168, overflowY: 'auto', border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--modal-input-bg)', padding: 6 }}>
                {imageModels.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 6px' }}>暂无已启用的图像模型</div>
                )}
                {imageModels.map(m => {
                  const checked = (form.modelIds || []).some(x => String(x) === String(m.id));
                  return (
                    <div key={m.id}
                      onClick={() => setForm(f => {
                        const cur = f.modelIds || [];
                        return { ...f, modelIds: checked ? cur.filter(x => String(x) !== String(m.id)) : [...cur, m.id] };
                      })}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px', borderRadius: 6, cursor: 'pointer', background: checked ? 'rgba(59,130,246,0.12)' : 'transparent' }}>
                      {checked ? <CheckCircle2 size={16} color="#10b981" style={{ flexShrink: 0 }} /> : <Circle size={16} color="#6b7280" style={{ flexShrink: 0 }} />}
                      {m.logo_url
                        ? <img src={m.logo_url} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain', flexShrink: 0 }} />
                        : <div style={{ width: 18, height: 18, flexShrink: 0 }} />}
                      <span style={{ fontSize: 12, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                已选 {(form.modelIds || []).length} 个{(form.modelIds || []).length === 0 ? '（节点内可选全部图像模型）' : ''}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-body)' }}
                onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}>
                {form.enabled ? <CheckCircle2 size={18} color="#10b981" /> : <Circle size={18} color="#6b7280" />}
                启用（用户可见）
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btn(true), opacity: saving ? 0.6 : 1 }}>{saving ? '保存中...' : (form.id ? '更新预设' : '新增预设')}</button>
              {form.id && <button onClick={() => newPreset()} style={btn(false)}>取消编辑</button>}
            </div>
          </div>
        </div>
      </div>

      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}
      {confirm && (
        <ConfirmModal
          title={confirm.kind === 'toolset' ? '删除工具集' : confirm.kind === 'group' ? '删除分组' : '删除预设'}
          message={confirm.kind === 'toolset'
            ? `确定删除工具集「${confirm.name}」？其下所有分组和预设都将被删除，不可恢复。`
            : confirm.kind === 'group'
              ? `确定删除分组「${confirm.name}」？组内所有预设将一并删除，不可恢复。`
              : `确定删除预设「${confirm.name}」？此操作不可恢复。`}
          confirmText="删除"
          onConfirm={handleDelete} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}


